import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import DataTable, { Column } from "../components/DataTable";
import StatTile from "../components/StatTile";
import SegmentedToggle from "../components/SegmentedToggle";
import { TagChip } from "../components/Pills";
import { CardGrid, ErrorBanner, LoadingNote, NoteCard } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { fetchEvents } from "../dashboardApi";
import { EventRow, EventsResponse } from "../types";
import { colors, type } from "../styles";
import { formatDate, formatNumber, formatRelative } from "../format";
import EventsCreatedView from "./events/EventsCreatedView";

/**
 * Individual events with their own attendee counts.
 *
 * This is a list screen, not more aggregate tiles — the Home tab already
 * carries the daily rollup of these same numbers ("products with
 * submissions/tickets"). What is new here is the per-event detail, which the
 * daily aggregate never stored.
 *
 * **The attendee count is the same number the Home tab shows.** Not a similar
 * one computed a similar way: the nightly scan calls `attendeesForEvent` once
 * per event and feeds both the daily aggregate and this row from that single
 * call. A claimed, accepted, non-organizer ticket — deliberately not `scanned`,
 * which only 1.4% of Markit tickets ever are. If this list and the Home tab ever
 * disagreed it would be a bug, not two perspectives, so they are built so they
 * cannot.
 *
 * **CACHED, and it costs nothing to be.** A live version would have to re-read
 * every event's tickets to know a count: 21k ticket reads at 30 days, 74.5k at
 * 100 (~12.3s). The nightly scan already reads all of that for the Home tab, so
 * persisting the rows it already computed adds zero Firestore reads and buys the
 * full 100-day window rather than the ~7 days a live query could afford.
 *
 * The legacy screen this replaces offered two fixed tabs — "Min. 1 Attendee" and
 * "Min. 10 Attendee" (which, despite its label, actually meant 11). Both are
 * preserved as filters, with the threshold tab corrected to the 20 the Home tab
 * and the Mixpanel metric it replaces both use.
 */

const splitParam = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

type MinFilter = "all" | "one" | "threshold";

/**
 * Two views over the same collection, kept apart because they answer different
 * questions over genuinely different populations.
 *
 * "Scheduled" lists products by when they START — the original screen, and what
 * an attendee count is attached to. "Created" ranks creators by when they MADE
 * things, which measurement showed is not a re-slicing of the same rows: 45% of
 * what is created in a 100-day window is scheduled beyond it and appears on the
 * first view not at all.
 *
 * A toggle rather than a second sidebar item: both are "Events", opened by the
 * same person for adjacent reasons, and a fourth Engagement entry for a view
 * this closely related would be the grab-bag `routes.ts` argues against.
 */
type EventsView = "scheduled" | "created";

const VIEWS: { key: EventsView; label: string }[] = [
  { key: "scheduled", label: "Scheduled" },
  { key: "created", label: "Created" },
];

const EventsScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const fetcher = useCallback(() => fetchEvents(), []);
  const { data, error, loading } = useDashboardData<EventsResponse>(fetcher, []);

  const view: EventsView = searchParams.get("view") === "created" ? "created" : "scheduled";
  const query = searchParams.get("q") ?? "";
  const min = (searchParams.get("min") as MinFilter) || "all";
  const types = splitParam(searchParams.get("type"));

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const events = useMemo(() => data?.events ?? [], [data]);
  const threshold = data?.attendeeThreshold ?? 20;

  const eventTypes = useMemo(
    // Array.from, not a spread: this repo's tsconfig targets below ES2015, so
    // spreading a Set needs --downlevelIteration.
    () => Array.from(new Set(events.map(event => event.eventType).filter(Boolean))).sort(),
    [events]
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return events.filter(event => {
      if (needle) {
        const haystack =
          `${event.title} ${event.creatorName} ${event.creatorUsername} ${event.id}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (min === "one" && event.attendees < 1) return false;
      if (min === "threshold" && event.attendees < threshold) return false;
      if (types.length && !types.includes(event.eventType)) return false;
      return true;
    });
  }, [events, query, min, types, threshold]);

  const syncedAt = data?.computedAt ?? undefined;
  const freshness = { kind: "cached" as const, updatedAt: syncedAt };

  const withAny = events.filter(event => event.attendees >= 1).length;
  const withThreshold = events.filter(event => event.attendees >= threshold).length;
  const totalAttendees = filtered.reduce((sum, event) => sum + event.attendees, 0);

  const columns: Column<EventRow>[] = [
    {
      key: "start",
      label: "When",
      sortValue: row => row.start || "",
      render: row => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span title={formatDate(row.start)}>{formatRelative(row.start)}</span>
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            {formatDate(row.start)}
          </span>
        </div>
      ),
    },
    {
      key: "title",
      label: "Event",
      sortValue: row => row.title.toLowerCase(),
      render: row => (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <a
            href={`https://markitai.com/e/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...type.bodyMedium, color: colors.ink, textDecoration: "none" }}
            title={row.title}
          >
            {row.title || "(untitled)"}
          </a>
          <span
            style={{ ...type.bodySmallRegular, color: colors.inkFaint }}
            title={row.id}
          >
            {row.id}
          </span>
        </div>
      ),
    },
    {
      key: "creator",
      label: "Creator",
      sortValue: row => (row.creatorName || row.creatorUsername).toLowerCase(),
      render: row =>
        row.creatorUsername ? (
          <a
            href={`https://markitai.com/u/${row.creatorUsername}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...type.bodyRegular, color: colors.ink, textDecoration: "none" }}
          >
            {row.creatorName || `@${row.creatorUsername}`}
          </a>
        ) : (
          <span style={{ color: colors.inkFaint }} title={row.createdBy}>
            —
          </span>
        ),
    },
    {
      key: "type",
      label: "Type",
      sortValue: row => row.eventType,
      render: row => (
        <span style={{ ...type.bodyRegular, color: colors.inkMuted }}>
          {row.eventType || "—"}
        </span>
      ),
    },
    {
      key: "attendees",
      label: "Attendees",
      numeric: true,
      sortValue: row => row.attendees,
      render: row => (
        <span
          style={{
            ...type.bodyMedium,
            color: row.attendees >= threshold ? colors.ink : colors.inkMuted,
          }}
        >
          {formatNumber(row.attendees)}
        </span>
      ),
    },
    {
      key: "tickets",
      label: "Tickets total",
      numeric: true,
      sortValue: row => row.ticketsTotal,
      // Context for the attendee count beside it: the gap between the two is
      // unclaimed, pending and organizer tickets.
      render: row => (
        <span style={{ color: colors.inkFaint }}>{formatNumber(row.ticketsTotal)}</span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Events"
        subtitle={
          view === "created"
            ? "Who is creating events, posts and products — and how recently."
            : "Individual events, posts and products with their own attendee counts."
        }
      />

      {/* Held in the URL like every other filter here, so a view is a shareable
          link rather than a state someone has to be talked into. */}
      <div style={{ marginBottom: 16 }}>
        <SegmentedToggle
          label="Events view"
          size="default"
          options={VIEWS}
          value={view}
          onChange={next => setParam("view", next === "scheduled" ? "" : next)}
        />
      </div>

      {view === "created" && <EventsCreatedView />}

      {view === "scheduled" && (
        <>
      {error && <ErrorBanner message={error} />}

      {data && (
        <div style={{ marginBottom: 16 }}>
          <CardGrid min={200}>
            <StatTile
              label="Events in window"
              value={formatNumber(events.length)}
              secondary={
                data.windowFrom
                  ? `Since ${formatDate(data.windowFrom)}`
                  : "Nightly rollup window"
              }
              freshness={freshness}
            />
            <StatTile
              label="With ≥1 attendee"
              value={formatNumber(withAny)}
              secondary="Claimed, accepted, non-organizer tickets"
              freshness={freshness}
            />
            <StatTile
              label={`With ${threshold}+ attendees`}
              value={formatNumber(withThreshold)}
              secondary={`Threshold ${threshold}, inclusive`}
              freshness={freshness}
            />
            <StatTile
              label="Attendees shown"
              value={formatNumber(totalAttendees)}
              secondary={
                filtered.length === events.length
                  ? "Across every event in the window"
                  : `Across the ${formatNumber(filtered.length)} filtered events`
              }
              freshness={freshness}
            />
          </CardGrid>
        </div>
      )}

      {/* Filters. Held in the URL so a filtered view is a shareable link, the
          same convention CustomersScreen uses. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <input
          value={query}
          onChange={event => setParam("q", event.target.value)}
          placeholder="Search title, creator or event ID"
          style={{
            ...type.bodyRegular,
            color: colors.ink,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: "8px 12px",
            minWidth: 280,
            outline: "none",
          }}
        />

        <div style={{ display: "flex", gap: 6 }}>
          {(
            [
              { key: "all", label: "All" },
              { key: "one", label: "Min. 1 attendee" },
              { key: "threshold", label: `Min. ${threshold} attendees` },
            ] as { key: MinFilter; label: string }[]
          ).map(option => (
            <button
              key={option.key}
              onClick={() => setParam("min", option.key === "all" ? "" : option.key)}
              style={{
                ...type.bodySmallMedium,
                color: min === option.key ? colors.surface : colors.inkMuted,
                background: min === option.key ? colors.ink : colors.surface,
                border: `1px solid ${min === option.key ? colors.ink : colors.border}`,
                borderRadius: 999,
                padding: "6px 12px",
                cursor: "pointer",
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        {eventTypes.length > 0 && (
          <select
            value={types[0] ?? ""}
            onChange={event => setParam("type", event.target.value)}
            style={{
              ...type.bodyRegular,
              color: colors.ink,
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            <option value="">All types</option>
            {eventTypes.map(eventType => (
              <option key={eventType} value={eventType}>
                {eventType}
              </option>
            ))}
          </select>
        )}

        <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
          {formatNumber(filtered.length)} of {formatNumber(events.length)}
        </span>

        {data && (
          <TagChip
            label={`Synced ${formatRelative(data.computedAt ?? "")}`}
            icon="ion:time-outline"
            title="Written by the nightly rollup — an event today shows its count as of last night's run."
          />
        )}
      </div>

      {loading && <LoadingNote />}

      {data && (
        <>
          {data.truncated && (
            <div style={{ marginBottom: 16 }}>
              <NoteCard title="List truncated">
                The nightly rollup stores a capped number of events per window and
                that cap was reached, so the oldest events in the window are not on
                this page.
              </NoteCard>
            </div>
          )}
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={row => row.id}
            initialSort={{ key: "start", direction: "desc" }}
            emptyLabel={
              events.length === 0
                ? "The nightly rollup has not written the event list yet."
                : "No events match these filters."
            }
          />

          <div style={{ marginTop: 16 }}>
            <NoteCard title="“Attendees” means a claimed, accepted ticket — not a door scan">
              An attendee is a ticket with an owner, accepted, minus the host&rsquo;s
              own organizer tickets. It is deliberately <strong>not</strong>{" "}
              <code>scanned</code>: only 1.4% of Markit tickets are ever scanned at
              the door, so a scan-based count would read as though nobody attends
              anything. This is the <strong>same number</strong>, from the same
              nightly pass, that the Home tab&rsquo;s &ldquo;products with
              submissions/tickets&rdquo; figure counts — the two cannot disagree.
              Eventbrite and Showclix imports are excluded, as are test-account
              events. Every other product type counts, which is why posts, webinars
              and video/PDF products appear here alongside ticketed events.
              {data.eventsScanned > 0 && (
                <>
                  {" "}
                  Last run scanned {formatNumber(data.eventsScanned)} events and{" "}
                  {formatNumber(data.ticketsScanned)} tickets.
                </>
              )}
            </NoteCard>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default EventsScreen;
